import { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Printer, ArrowLeft, QrCode, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useNavigate } from 'react-router-dom';

const SITE_URL = window.location.origin;

export default function QrCodePrintPage() {
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [tableFrom, setTableFrom] = useState(1);
  const [tableTo, setTableTo] = useState(30);
  const [size, setSize] = useState('medium');

  const sizes = { small: { qr: 100, card: 'w-40' }, medium: { qr: 140, card: 'w-52' }, large: { qr: 180, card: 'w-64' } };
  const s = sizes[size];

  const tables = [];
  for (let i = tableFrom; i <= tableTo; i++) tables.push(i);

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const printWin = window.open('', '_blank');
    printWin.document.write(`<!DOCTYPE html><html><head><title>QR-Codes - Little Italy</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Helvetica Neue', Arial, sans-serif; background: #fff; }
        .grid { display: flex; flex-wrap: wrap; gap: 24px; padding: 24px; justify-content: center; }
        .card { border: 2px solid #e8e0d4; border-radius: 16px; padding: 20px 16px; text-align: center; page-break-inside: avoid; background: #fff; }
        .card h3 { font-family: Georgia, serif; font-size: 14px; color: #8b4a2b; margin-bottom: 4px; }
        .card .num { font-family: Georgia, serif; font-size: 32px; font-weight: 700; color: #2d2016; margin: 6px 0; }
        .card .sub { font-size: 10px; color: #8a7b6b; letter-spacing: 0.5px; }
        .card svg { margin: 10px auto; display: block; }
        .card .url { font-size: 8px; color: #b5a899; margin-top: 8px; word-break: break-all; }
        .divider { width: 40px; height: 2px; background: #c5a346; margin: 8px auto; border-radius: 1px; }
        @media print { body { margin: 0; } .grid { gap: 16px; padding: 16px; } }
      </style></head><body>
      <div class="grid">${content.innerHTML}</div>
    </body></html>`);
    printWin.document.close();
    setTimeout(() => { printWin.print(); }, 500);
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="qr-code-print-page">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="back-to-admin">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Button>
            <span className="text-stone-300">|</span>
            <h1 className="font-serif text-lg text-stone-800 flex items-center gap-2"><QrCode className="w-5 h-5 text-amber-700" /> QR-Codes drucken</h1>
          </div>
          <Button onClick={handlePrint} className="bg-amber-700 hover:bg-amber-800" data-testid="print-btn">
            <Printer className="w-4 h-4 mr-2" /> Drucken
          </Button>
        </div>
      </div>

      {/* Settings */}
      <div className="max-w-5xl mx-auto px-4 py-4">
        <Card className="border-stone-200 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex items-center gap-2">
                <Settings2 className="w-4 h-4 text-stone-400" />
                <span className="text-sm font-medium text-stone-600">Einstellungen</span>
              </div>
              <div>
                <label className="text-xs text-stone-500 block mb-1">Von Tisch</label>
                <Input type="number" value={tableFrom} onChange={e => setTableFrom(Math.max(1, parseInt(e.target.value) || 1))} min={1} max={30} className="w-20 h-9" data-testid="table-from" />
              </div>
              <div>
                <label className="text-xs text-stone-500 block mb-1">Bis Tisch</label>
                <Input type="number" value={tableTo} onChange={e => setTableTo(Math.min(30, parseInt(e.target.value) || 30))} min={1} max={30} className="w-20 h-9" data-testid="table-to" />
              </div>
              <div>
                <label className="text-xs text-stone-500 block mb-1">Größe</label>
                <Select value={size} onValueChange={setSize}>
                  <SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Klein</SelectItem>
                    <SelectItem value="medium">Mittel</SelectItem>
                    <SelectItem value="large">Groß</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <span className="text-sm text-stone-400">{tables.length} QR-Codes</span>
            </div>
          </CardContent>
        </Card>

        {/* QR Code Grid (visible preview + print source) */}
        <div className="flex flex-wrap gap-4 justify-center" ref={printRef} data-testid="qr-grid">
          {tables.map(tn => (
            <div key={tn} className="card" style={{ textAlign: 'center', border: '2px solid #e8e0d4', borderRadius: '16px', padding: '20px 16px', background: '#fff' }}>
              <h3 style={{ fontFamily: 'Georgia, serif', fontSize: '14px', color: '#8b4a2b', marginBottom: '4px' }}>Ristorante Little Italy</h3>
              <div className="divider" style={{ width: '40px', height: '2px', background: '#c5a346', margin: '8px auto', borderRadius: '1px' }}></div>
              <div className="num" style={{ fontFamily: 'Georgia, serif', fontSize: '32px', fontWeight: 700, color: '#2d2016', margin: '6px 0' }}>Tisch {tn}</div>
              <p className="sub" style={{ fontSize: '10px', color: '#8a7b6b', letterSpacing: '0.5px' }}>Scannen Sie den Code um zu bestellen</p>
              <div style={{ margin: '10px auto', display: 'flex', justifyContent: 'center' }}>
                <QRCodeSVG
                  value={`${SITE_URL}/t/${tn}`}
                  size={s.qr}
                  level="M"
                  bgColor="#ffffff"
                  fgColor="#2d2016"
                />
              </div>
              <p className="url" style={{ fontSize: '8px', color: '#b5a899', marginTop: '8px' }}>{SITE_URL}/t/{tn}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
